<template>
  <div class="growth-card" :class="{ 'growth-card--loading': loading }">
    <div v-if="loading" class="growth-card__spinner">
      <div class="spinner-border spinner-border-sm text-secondary" role="status">
        <span class="visually-hidden">Laster...</span>
      </div>
    </div>
    <template v-else>
      <div class="growth-card__header">
        <span class="growth-card__period">{{ period }}</span>
        <div
          class="growth-card__trend"
          :class="{
            'growth-card__trend--positive': growth > 0,
            'growth-card__trend--negative': growth < 0,
            'growth-card__trend--neutral': growth === 0 || growth === null
          }"
        >
          <font-awesome-icon
            v-if="growth !== null && growth !== 0"
            :icon="growth > 0 ? 'arrow-up' : 'arrow-down'"
          />
          <span v-if="growth !== null">{{ Math.abs(growth) }}%</span>
          <span v-else>--</span>
        </div>
      </div>
      <div class="growth-card__values">
        <div class="growth-card__current">
          <span class="growth-card__label">{{ currentLabel }}</span>
          <span class="growth-card__value">{{ formatValue(currentValue) }}</span>
        </div>
        <div class="growth-card__divider"></div>
        <div class="growth-card__previous">
          <span class="growth-card__label">{{ previousLabel }}</span>
          <span class="growth-card__value growth-card__value--muted">{{ formatValue(previousValue) }}</span>
        </div>
      </div>
      <div class="growth-card__bar">
        <div
          class="growth-card__bar-current"
          :style="{ width: currentBarWidth + '%' }"
        ></div>
        <div
          class="growth-card__bar-previous"
          :style="{ width: previousBarWidth + '%' }"
        ></div>
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: 'GrowthMetricCard',
  props: {
    period: { type: String, default: 'Periode' },
    currentLabel: { type: String, default: 'Nåværende' },
    previousLabel: { type: String, default: 'Forrige' },
    currentValue: { type: Number, default: 0 },
    previousValue: { type: Number, default: 0 },
    growth: { type: Number, default: null },
    loading: { type: Boolean, default: false },
    format: { type: String, default: 'currency' } // 'currency', 'number', 'percent'
  },
  computed: {
    maxValue() {
      return Math.max(this.currentValue, this.previousValue);
    },
    currentBarWidth() {
      if (this.maxValue === 0) return 0;
      return (this.currentValue / this.maxValue) * 100;
    },
    previousBarWidth() {
      if (this.maxValue === 0) return 0;
      return (this.previousValue / this.maxValue) * 100;
    }
  },
  methods: {
    formatValue(value) {
      if (this.format === 'currency') {
        return new Intl.NumberFormat('nb-NO').format(Math.round(value)) + ' kr';
      } else if (this.format === 'percent') {
        return value.toFixed(1) + '%';
      }
      return new Intl.NumberFormat('nb-NO').format(Math.round(value));
    }
  }
};
</script>

<style scoped>
.growth-card {
  background: var(--bo-surface);
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  min-height: 160px;
}

.growth-card--loading {
  display: flex;
  align-items: center;
  justify-content: center;
}

.growth-card__spinner {
  display: flex;
  align-items: center;
  justify-content: center;
}

.growth-card__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.growth-card__period {
  font-size: 14px;
  font-weight: 600;
  color: var(--bo-ink);
}

.growth-card__trend {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 20px;
}

.growth-card__trend--positive {
  background: rgba(16, 185, 129, 0.1);
  color: #10b981;
}

.growth-card__trend--negative {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

.growth-card__trend--neutral {
  background: var(--bo-surface-2);
  color: var(--bo-muted);
}

.growth-card__values {
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
}

.growth-card__current,
.growth-card__previous {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.growth-card__divider {
  width: 1px;
  background: var(--bo-line);
}

.growth-card__label {
  font-size: 12px;
  color: var(--bo-muted-2);
}

.growth-card__value {
  font-size: 18px;
  font-weight: 700;
  color: var(--bo-ink);
}

.growth-card__value--muted {
  color: var(--bo-muted);
  font-weight: 500;
}

.growth-card__bar {
  position: relative;
  height: 8px;
  background: var(--bo-surface-2);
  border-radius: 4px;
  overflow: hidden;
}

.growth-card__bar-current {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  background: #B30E10;
  border-radius: 4px;
  z-index: 2;
  transition: width 0.3s ease;
}

.growth-card__bar-previous {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  background: var(--bo-line);
  border-radius: 4px;
  z-index: 1;
  transition: width 0.3s ease;
}
</style>
